<template>
  <question-base-wrapper
    v-bind="baseWrapperProps"
    :class="{
      'counter-list-base-wrapper': isColumnLayout
    }"
    :help-text="helpTextCommon"
  >
    <validation-provider
      v-slot="{ errors, failed, failedRules }"
      :vid="`${stepId}_${questionId}`"
      :name="questionId"
      :rules="validationRules ? validationRules['field'] : 'required'"
      :data-unit-test="`${stepId}_${questionId}`"
    >
      <input
        type="hidden"
        :value="commonCountersValue"
      >
      <div
        v-if="failed && errors && errors.length"
        class="error--text fz12 mt-6"
      >
        <p>{{ getFieldErrors('field', failedRules, errors)[0] }}</p>
      </div>
    </validation-provider>
    <ul
      class="counter-list"
    >
      <li
        v-for="(counter, index) in counterList"
        :key="`${counter}_${index}`"
        class="list-item"
      >
        <help-text
          v-if="isColumnLayout"
          class="mb-6"
        >
          <span v-html="helpText[index]" />
        </help-text>

        <span
          v-if="optionsConfig.options[counter].label"
          class="item-label"
        >{{ optionsConfig.options[counter].label }}</span>
        <quantity
          v-model.number="questionData[counter]"
          :max="optionsConfig.options[counter].max"
          :min="optionsConfig.options[counter].min || 0"
          :max-with-plus="maxWithPlus"
          :data-test="snakeCase(counter)"
        />
      </li>
    </ul>
  </question-base-wrapper>
</template>

<script>
import QuestionBase from './Base';
import QuestionBaseWrapper from './BaseWrapper';
import Quantity from '@/components/Quantity/Quantity';
import { validate as veeValidate } from 'vee-validate';

export default {
  name: 'question-base-counter-list',
  extends: QuestionBase,
  components: { QuestionBaseWrapper, Quantity },
  props: {
    maxWithPlus: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      fields: this.customFields,
      counterList: [...this.customFields],
    };
  },
  created () {
    const fields = this.getShowRulesDependentFields();

    this.validateFieldsRender(fields);
  },
  computed: {
    commonCountersValue () {
      let qty = 0;

      this.customFields.forEach(field => {
        if (this.questionData[field] > 0) {
          qty += this.questionData[field];
        }
      });

      return qty;
    },
    helpTextCommon () {
      if (!this.isColumnLayout) {
        return this.helpText;
      }

      return this.helpText.reduce((acc, key) => {
        acc += key;

        return acc;
      }, '');
    },
  },
  methods: {
    getShowRulesDependentFields () {
      const fields = [];
      const options = this.optionsConfig.options;

      for (const option in options) {
        if (Object.prototype.hasOwnProperty.call(options[option], 'showRules')) {
          Object.keys(options[option].showRules || {}).forEach(rule => {
            fields.push(rule);
          });
        }
      }

      // return show rule dependent field names
      // need to watch if such field was changed
      return [...new Set(fields)];
    },
    validateFieldsRender (fields) {
      const options = this.optionsConfig.options;

      fields.forEach(field => {
        // add watchers for each show rule dependent field
        this.$watch(`stepData.${field}`, async val => {
          for (const option in options) {
            if (Object.prototype.hasOwnProperty.call(options[option], 'showRules')) {
              // validate each showRule
              const { valid } = await veeValidate(val, options[option].showRules[field]);

              if (!valid) {
                // if it is not valid show only valid fields
                // hidden fields reset to null
                this.counterList = this.counterList.filter(counter => counter !== option);
                this.questionData[option] = null;
              } else if (this.fields.length !== this.counterList.length) {
                // if have session data reset to value from API response
                // if value is null set 0 because quantity component expect number
                if (this.sessionData[option]) {
                  this.questionData[option] = this.sessionData[option];
                } else {
                  this.questionData[option] = 0;
                }

                const key = Object.keys(options).indexOf(option);
                this.counterList.splice(key, 0, option);
              }
            };
          }
        }, { immediate: true });
      });
    },
  },
};
</script>

<style scoped lang="scss">
.counter-list {
  padding: 0;

  .list-item {
    display: flex;
    align-items: center;

    .item-label {
      max-width: 30%;
      width: 100%;
      font-size: 16px;
      font-weight: bold;
    }

    & + .list-item {
      margin-top: 20px;
    }

    .quantity {
      width: 100%;
    }
  }
}

.counter-list-base-wrapper::v-deep {
  .help-text {
    display: none;

    @media screen and (min-width: 767px) {
      display: block;
    }
  }

  .counter-list {
    .list-item {
      flex-direction: column;
      align-items: start;

      @media screen and (min-width: 768px) {
        justify-content: center;

        .quantity {
          width: 100%;
          max-width: 100%;
        }
      }

      .help-text {
        display: block;

        @media screen and (min-width: 768px) {
          display: none;
        }
      }

      .item-label {
        display: none;

        @media screen and (min-width: 768px) {
          display: flex;
          justify-content: center;
          width: 100%;
          max-width: 100%;
          margin-bottom: 8px;
        }
      }
    }
  }
}
</style>
